/**
 * Grail search: <search-dialog> opens the header search overlay from any
 * [data-search-open] link (or the "/" key); <predictive-search> fetches
 * product, collection and query suggestions through the Section Rendering
 * API (section "predictive-search") into a labelled region, announces the
 * result count in a live status, and handles keyboard navigation:
 * ArrowDown moves into the results, ArrowUp/Down walks them, Escape closes
 * the results first and the overlay second.
 */
(() => {
  if (customElements.get('predictive-search')) return;

  const isTyping = (el) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

  class SearchDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('[data-search-input]');
      if (!this.dialog) return;

      this.onDocumentClick = (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onDocumentKeydown = (event) => {
        if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
        if (isTyping(event.target) || this.dialog.open || document.querySelector('dialog[open]')) return;
        event.preventDefault();

        // The open mobile menu makes the rest of the page (this overlay too) inert: close it first
        // and return focus to its toggle when the search closes.
        const menu = [...document.querySelectorAll('header-drawer')].find((drawer) => drawer.isOpen);
        if (menu) {
          menu.close(false);
          this.open(menu.summary);
          return;
        }
        this.open(document.activeElement);
      };
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      // Listeners on the dialog survive a move (theme editor reorder): add them once.
      if (this.bound) return;
      this.bound = true;
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-search-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => {
        const opener = this.opener;
        this.opener = null;
        if (opener && opener.isConnected) opener.focus({ preventScroll: true });
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener instanceof HTMLElement ? opener : null;
      this.dialog.showModal();
      this.input?.focus();
      this.input?.select();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-search-status]');
      this.suggestions = this.querySelector('[data-search-suggestions]');
      this.resetButton = this.querySelector('[data-search-reset]');
      this.resetButton.hidden = this.term === '';

      if (this.bound) return; // moved in the DOM (theme editor): listeners are still attached
      this.bound = true;
      this.cache = new Map();

      let timer;
      this.input.addEventListener('input', () => {
        this.resetButton.hidden = this.term === '';
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 220);
      });

      this.querySelector('form').addEventListener('reset', () => {
        clearTimeout(timer);
        setTimeout(() => {
          // A reset restores the input's default value (search.terms on /search): clear it for real.
          this.input.value = '';
          this.abortController?.abort();
          this.resetButton.hidden = true;
          this.closeResults();
          this.status.textContent = '';
          this.input.focus();
        });
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event));

      this.closest('dialog')?.addEventListener('cancel', (event) => {
        if (!this.suppressCancel) return;
        event.preventDefault();
        this.suppressCancel = false;
      });
    }

    get term() {
      return this.input.value.trim();
    }

    get items() {
      return this.results.hidden ? [] : [...this.results.querySelectorAll('[data-result]')];
    }

    async search() {
      const term = this.term;
      // Whatever happens next, an older request must not land on top of it.
      this.abortController?.abort();
      this.abortController = null;

      if (!term) {
        this.removeAttribute('aria-busy');
        this.closeResults();
        this.status.textContent = '';
        return;
      }
      if (this.cache.has(term)) {
        this.removeAttribute('aria-busy');
        this.render(this.cache.get(term));
        return;
      }

      const controller = new AbortController();
      this.abortController = controller;
      this.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][prefix]', 'last');

        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        const markup = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-results]');
        if (!markup) throw new Error('No results markup');

        const result = { html: markup.innerHTML, status: markup.dataset.status || '' };
        this.cache.set(term, result);
        if (term === this.term) this.render(result);
      } catch (error) {
        // Errors for a request that has been superseded (or terms that changed) are stale.
        if (error.name === 'AbortError' || controller !== this.abortController || term !== this.term) return;
        this.closeResults();
        this.status.textContent = this.dataset.errorText || '';
      } finally {
        if (controller === this.abortController) {
          this.abortController = null;
          this.removeAttribute('aria-busy');
        }
      }
    }

    render({ html, status }) {
      this.results.innerHTML = html;
      this.results.hidden = false;
      if (this.suggestions) this.suggestions.hidden = true;
      this.status.textContent = status;
    }

    closeResults() {
      this.results.hidden = true;
      if (this.suggestions) this.suggestions.hidden = false;
    }

    onKeydown(event) {
      const items = this.items;
      const index = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          if (event.target !== this.input && index === -1) return;
          if (!items.length) {
            if (event.target === this.input && this.term && this.cache.has(this.term)) {
              event.preventDefault();
              this.render(this.cache.get(this.term));
            }
            return;
          }
          event.preventDefault();
          items[Math.min(index + 1, items.length - 1)].focus();
          break;
        case 'ArrowUp':
          if (index === -1) return;
          event.preventDefault();
          if (index === 0) this.input.focus();
          else items[index - 1].focus();
          break;
        case 'Escape':
          if (this.results.hidden) {
            // With the results closed, Escape closes the search. Without this a
            // type="search" input spends the press clearing its text first, so
            // closing took three presses instead of two.
            const dialog = this.closest('dialog');
            if (!dialog?.open) return;
            event.preventDefault();
            dialog.close();
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          this.suppressCancel = true;
          setTimeout(() => (this.suppressCancel = false));
          this.closeResults();
          this.input.focus();
          break;
        default:
          break;
      }
    }
  }

  customElements.define('search-dialog', SearchDialog);
  customElements.define('predictive-search', PredictiveSearch);
})();
